/**
 * useShiftDuration - Hook for shift duration auto-calculation
 * Handles bidirectional calculation between start_time, end_time, and duration
 *
 * PROMPT 32: Shift Duration Auto-Calculation & Defaults
 *
 * EVENTSFLO-SHIFT-TIME-MATHS-SHARED-UTILITY-ROLLOUT-V1
 *   Internal time parsing/formatting delegated to `lib/shiftTimeMath`
 *   so the hook shares the platform-wide overnight + decimal semantics.
 *   Public API (state shape + setters) is intentionally unchanged.
 */
import { useState, useCallback } from "react";
import {
  normaliseTimeInput,
  calculateEndTime as _calcEndTime,
  calculateDurationHours as _calcDurationHours,
} from "../lib/shiftTimeMath";

// Default duration in minutes (8 hours)
const DEFAULT_DURATION_MINUTES = 8 * 60;

/**
 * Parse time string (HH:MM) to minutes since midnight.
 * Returns null for invalid input.
 */
const parseTimeToMinutes = (timeStr) => {
  const normalised = normaliseTimeInput(timeStr);
  if (!normalised) return null;
  const [h, m] = normalised.split(":").map(Number);
  return h * 60 + m;
};

/**
 * Format minutes since midnight to time string (HH:MM).
 * Overnight-aware (wraps).
 */
const formatMinutesToTime = (totalMinutes) => {
  if (totalMinutes === null || totalMinutes === undefined) return "";
  // Delegate to the shared utility via a known anchor + duration.
  const result = _calcEndTime("00:00", totalMinutes / 60);
  return result || "";
};

/**
 * Parse duration string (HH:MM) to minutes
 */
const parseDurationToMinutes = (durationStr) => {
  if (!durationStr || typeof durationStr !== "string") return null;
  const match = durationStr.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/**
 * Format minutes to duration string (HH:MM)
 */
const formatMinutesToDuration = (totalMinutes) => {
  if (totalMinutes === null || totalMinutes === undefined || totalMinutes < 0) return "";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/**
 * Calculate duration in minutes between start and end time.
 * Handles overnight shifts via the shared utility.
 */
const calculateDuration = (startMinutes, endMinutes) => {
  if (startMinutes === null || endMinutes === null) return null;
  const startStr = formatMinutesToTime(startMinutes);
  const endStr = formatMinutesToTime(endMinutes);
  const hours = _calcDurationHours(startStr, endStr);
  if (hours == null) return null;
  return Math.round(hours * 60);
};

/**
 * useShiftDuration Hook
 * 
 * @param {Object} options
 * @param {string} options.initialStartTime - Initial start time (HH:MM)
 * @param {string} options.initialEndTime - Initial end time (HH:MM)
 * @param {boolean} options.useDefaults - Whether to apply default duration on init
 * @returns {Object} State and handlers for duration management
 */
export function useShiftDuration({
  initialStartTime = "",
  initialEndTime = "",
  useDefaults = true,
} = {}) {
  // Calculate initial state values
  const getInitialState = () => {
    if (useDefaults && !initialStartTime && !initialEndTime) {
      // Set default start time (09:00) and calculate end based on default duration
      const defaultStart = "09:00";
      const defaultEnd = formatMinutesToTime(parseTimeToMinutes(defaultStart) + DEFAULT_DURATION_MINUTES);
      return {
        startTime: defaultStart,
        endTime: defaultEnd,
        duration: formatMinutesToDuration(DEFAULT_DURATION_MINUTES),
        isEndTimeCalculated: true,
        isDurationCalculated: false,
      };
    } else if (initialStartTime && initialEndTime) {
      // Calculate duration from provided times
      const startMinutes = parseTimeToMinutes(initialStartTime);
      const endMinutes = parseTimeToMinutes(initialEndTime);
      const durationMinutes = calculateDuration(startMinutes, endMinutes);
      return {
        startTime: initialStartTime,
        endTime: initialEndTime,
        duration: durationMinutes !== null ? formatMinutesToDuration(durationMinutes) : "",
        isEndTimeCalculated: false,
        isDurationCalculated: durationMinutes !== null,
      };
    } else if (initialStartTime) {
      // Apply default duration
      const startMinutes = parseTimeToMinutes(initialStartTime);
      if (startMinutes !== null) {
        const endMinutes = startMinutes + DEFAULT_DURATION_MINUTES;
        return {
          startTime: initialStartTime,
          endTime: formatMinutesToTime(endMinutes),
          duration: formatMinutesToDuration(DEFAULT_DURATION_MINUTES),
          isEndTimeCalculated: true,
          isDurationCalculated: false,
        };
      }
    }
    return {
      startTime: initialStartTime,
      endTime: initialEndTime,
      duration: "",
      isEndTimeCalculated: false,
      isDurationCalculated: false,
    };
  };
  
  const initialState = getInitialState();
  
  // Core time values
  const [startTime, setStartTimeInternal] = useState(initialState.startTime);
  const [endTime, setEndTimeInternal] = useState(initialState.endTime);
  const [duration, setDurationInternal] = useState(initialState.duration);
  
  // Track which field was last manually edited
  // "start" | "end" | "duration" | null
  const [lastEdited, setLastEdited] = useState(null);
  
  // Track if values are auto-calculated
  const [isEndTimeCalculated, setIsEndTimeCalculated] = useState(initialState.isEndTimeCalculated);
  const [isDurationCalculated, setIsDurationCalculated] = useState(initialState.isDurationCalculated);
  
  /**
   * Handle start time change
   * Recalculates end_time to maintain current duration
   */
  const setStartTime = useCallback((newStartTime) => {
    setStartTimeInternal(newStartTime);
    setLastEdited("start");
    setIsEndTimeCalculated(false);
    
    const startMinutes = parseTimeToMinutes(newStartTime);
    if (startMinutes === null) return;
    
    // Get current duration
    const durationMinutes = parseDurationToMinutes(duration);
    
    if (durationMinutes !== null && durationMinutes > 0) {
      // Recalculate end time based on duration
      const newEndMinutes = startMinutes + durationMinutes;
      setEndTimeInternal(formatMinutesToTime(newEndMinutes));
      setIsEndTimeCalculated(true);
    } else {
      // No valid duration, try to calculate from existing end time
      const endMinutes = parseTimeToMinutes(endTime);
      if (endMinutes !== null) {
        const newDuration = calculateDuration(startMinutes, endMinutes);
        if (newDuration !== null && newDuration > 0) {
          setDurationInternal(formatMinutesToDuration(newDuration));
          setIsDurationCalculated(true);
        }
      }
    }
  }, [duration, endTime]);
  
  /**
   * Handle end time change
   * Recalculates duration based on start and new end time
   */
  const setEndTime = useCallback((newEndTime) => {
    setEndTimeInternal(newEndTime);
    setLastEdited("end");
    setIsEndTimeCalculated(false);
    
    const startMinutes = parseTimeToMinutes(startTime);
    const endMinutes = parseTimeToMinutes(newEndTime);
    
    if (startMinutes !== null && endMinutes !== null) {
      const newDuration = calculateDuration(startMinutes, endMinutes);
      if (newDuration !== null && newDuration >= 0) {
        setDurationInternal(formatMinutesToDuration(newDuration));
        setIsDurationCalculated(true);
      }
    }
  }, [startTime]);
  
  /**
   * Handle duration change
   * Recalculates end_time based on start time and new duration
   */
  const setDuration = useCallback((newDuration) => {
    setDurationInternal(newDuration);
    setLastEdited("duration");
    setIsDurationCalculated(false);
    
    const startMinutes = parseTimeToMinutes(startTime);
    const durationMinutes = parseDurationToMinutes(newDuration);
    
    if (startMinutes !== null && durationMinutes !== null && durationMinutes > 0) {
      const newEndMinutes = startMinutes + durationMinutes;
      setEndTimeInternal(formatMinutesToTime(newEndMinutes));
      setIsEndTimeCalculated(true);
    }
  }, [startTime]);
  
  /**
   * Reset all values
   */
  const reset = useCallback((newStartTime = "", newEndTime = "") => {
    setStartTimeInternal(newStartTime);
    setEndTimeInternal(newEndTime);
    setLastEdited(null);
    setIsEndTimeCalculated(false);
    setIsDurationCalculated(false);
    
    if (newStartTime && newEndTime) {
      const startMinutes = parseTimeToMinutes(newStartTime);
      const endMinutes = parseTimeToMinutes(newEndTime);
      const durationMinutes = calculateDuration(startMinutes, endMinutes);
      if (durationMinutes !== null) {
        setDurationInternal(formatMinutesToDuration(durationMinutes));
        setIsDurationCalculated(true);
      }
    } else if (newStartTime) {
      // Apply default duration
      const startMinutes = parseTimeToMinutes(newStartTime);
      if (startMinutes !== null) {
        const endMinutes = startMinutes + DEFAULT_DURATION_MINUTES;
        setEndTimeInternal(formatMinutesToTime(endMinutes));
        setDurationInternal(formatMinutesToDuration(DEFAULT_DURATION_MINUTES));
        setIsEndTimeCalculated(true);
      }
    } else {
      setDurationInternal("");
    }
  }, []);
  
  /**
   * Get duration in decimal hours (for API if needed)
   */
  const getDurationHours = useCallback(() => {
    const minutes = parseDurationToMinutes(duration);
    if (minutes === null) return null;
    return minutes / 60;
  }, [duration]);
  
  /**
   * Check if all values are valid
   */
  const isValid = useCallback(() => {
    const startMinutes = parseTimeToMinutes(startTime);
    const endMinutes = parseTimeToMinutes(endTime);
    const durationMinutes = parseDurationToMinutes(duration);
    
    return startMinutes !== null && endMinutes !== null && durationMinutes !== null && durationMinutes > 0;
  }, [startTime, endTime, duration]);
  
  return {
    // Values
    startTime,
    endTime,
    duration,
    
    // Setters
    setStartTime,
    setEndTime,
    setDuration,
    reset,
    
    // Calculated state indicators
    isEndTimeCalculated,
    isDurationCalculated,
    lastEdited,
    
    // Utilities
    getDurationHours,
    isValid,
  };
}

// Export utility functions for use elsewhere
export {
  parseTimeToMinutes,
  formatMinutesToTime,
  parseDurationToMinutes,
  formatMinutesToDuration,
  calculateDuration,
  DEFAULT_DURATION_MINUTES,
};

export default useShiftDuration;
